<template>
	<div class="hris-page">
		<div class="hris-page-inner">
			<div class="hris-page-header">
				<span class="hris-page-eyebrow">Timekeeping</span>
				<h1 class="hris-page-title">Periodic Table</h1>
				<p class="hris-page-subtitle">Manage payroll cut-offs, periods, and pay dates for each year.</p>
			</div>

			<div class="hris-toolbar">
				<q-select v-model="yearfilter" :options="yearOptions" label="Year" outlined dense color="green" style="min-width: 150px;"></q-select>
				<q-btn unelevated class="hris-q-primary" label="Filter" @click="filterData"></q-btn>
				<div class="hris-toolbar-spacer"></div>
				<q-btn unelevated class="hris-q-outline" label="Create" @click="createCutOff"></q-btn>
			</div>

			<div class="hris-panel">
				<q-table :dense="$q.screen.lt.md" :grid="$q.screen.xs" :data="generatedData"
					:columns="columns" :pagination="initialPagination" row-key="id" flat>
					<template v-slot:body="props">
							<q-tr slot="body" :props="props" @click="selectedRow(props)" style="cursor: pointer;">
								<q-td key="months" :props="props">{{ props.row.months }}</q-td>
								<q-td key="cut_off" :props="props">{{ props.row.cut_off }}</q-td>
								<q-td key="payroll_cutoff" :props="props">
									<div class="text-weight-medium">{{ props.row.payroll_cutoff }}</div>
									<div v-if="props.row.group_schedules && props.row.group_schedules.length > 0" class="q-mt-xs">
										<div
											v-for="(schedule, idx) in props.row.group_schedules"
											:key="`cutoff-${idx}`"
											class="text-caption text-grey-8 q-mb-xs"
										>
											<span v-if="schedule.payroll_cutoff_from && schedule.payroll_cutoff_to">
												{{ schedule.payroll_cutoff_from }} to {{ schedule.payroll_cutoff_to }}
											</span>
											<span v-if="schedule.payroll_group" class="hris-badge hris-badge-accent q-ml-xs">{{ schedule.payroll_group }}</span>
										</div>
									</div>
								</q-td>
								<q-td key="payroll_period" :props="props">
									<div class="text-weight-medium">{{ props.row.payroll_period }}</div>
									<div v-if="props.row.group_schedules && props.row.group_schedules.length > 0" class="q-mt-xs">
										<div
											v-for="(schedule, idx) in props.row.group_schedules"
											:key="`period-${idx}`"
											class="text-caption text-grey-8 q-mb-xs"
										>
											<span>{{ schedule.payroll_period }}</span>
											<span v-if="schedule.payroll_group" class="hris-badge hris-badge-accent q-ml-xs">{{ schedule.payroll_group }}</span>
										</div>
									</div>
								</q-td>
								<q-td key="paydate" :props="props">
									<div class="text-weight-medium">{{ props.row.paydate }}</div>
									<div v-if="props.row.group_schedules && props.row.group_schedules.length > 0" class="q-mt-xs">
									<div
										v-for="(schedule, idx) in props.row.group_schedules"
										:key="idx"
										class="text-caption text-grey-8 q-mb-xs"
									>
										<div>
											{{ schedule.pay_date }}
											<span v-if="schedule.payroll_group" class="hris-badge hris-badge-accent q-ml-xs">{{ schedule.payroll_group }}</span>
										</div>
									</div>
								</div>
							</q-td>
						</q-tr>
					</template>
					<template v-slot:no-data>
						<div class="hris-empty">No periodic records found.</div>
					</template>
					<template v-slot:top-right>
						<q-btn unelevated class="hris-q-outline" label="Download Excel" no-caps style="margin-right: 10px;"
							@click="exportTable('xlsx')"></q-btn>
						<q-btn unelevated class="hris-q-primary" label="Download CSV" no-caps @click="exportTable('csv')"></q-btn>
					</template>
				</q-table>
			</div>

			<q-dialog v-model="spinner" persistent>
				<div class="hris-loading" style="background: transparent; min-width: 120px;">
					<q-spinner color="green" size="48px"></q-spinner>
				</div>
			</q-dialog>

			<q-dialog v-model="addDialog" persistent transition-show="scale">
				<div class="hris-modal">
					<div class="hris-modal-header">
						<h2 class="hris-modal-title">Create Cut-Off</h2>
					</div>
					<div class="hris-modal-body">
						<q-form @submit="onSubmit">
							<div class="hris-form-row">
								<div class="hris-form-field">
									<q-input outlined dense color="green" v-model="year" label="Year"></q-input>
								</div>
								<div class="hris-form-field">
									<q-select outlined dense color="green" v-model="months" :options="monthOptions" transition-show="flip-up"
										label="Month" emit-value map-options option-value="id" option-label="name"></q-select>
								</div>
								<div class="hris-form-field">
									<q-select outlined dense color="green" v-model="cut_off" :options="cutOffOptions" transition-show="flip-up"
										label="Cut Off" emit-value map-options option-value="id" option-label="name"></q-select>
								</div>
							</div>

							<div class="hris-form-row">
								<div class="hris-form-field">
									<q-input outlined dense color="green" v-model="payroll_cutoff_from" mask="date" :rules="['date']"
										label="Payroll From">
										<template v-slot:append>
											<i class="far fa-calendar-alt cursor-pointer">
												<q-popup-proxy ref="dhDateProxy" transition-show="scale"
													transition-hide="scale">
													<q-date v-model="payroll_cutoff_from"
														@input="() => $refs.dhDateProxy.hide()"></q-date>
												</q-popup-proxy>
											</i>
										</template>
									</q-input>
								</div>
								<div class="hris-form-field">
									<q-input outlined dense color="green" v-model="payroll_cutoff_to" mask="date" :rules="['date']"
										label="Payroll To">
										<template v-slot:append>
											<i class="far fa-calendar-alt cursor-pointer">
												<q-popup-proxy ref="dhDateProxy" transition-show="scale"
													transition-hide="scale">
													<q-date v-model="payroll_cutoff_to"
														@input="() => $refs.dhDateProxy.hide()"></q-date>
												</q-popup-proxy>
											</i>
										</template>
									</q-input>
								</div>
							</div>

							<div class="hris-form-row">
								<div class="hris-form-field">
									<q-input outlined dense color="green" v-model="payroll_period" label="Payroll Period" lazy-rules
										:rules="[val => val && val.length > 0 || 'Required']"></q-input>
								</div>
								<div class="hris-form-field">
									<q-input outlined dense color="green" v-model="pay_date" mask="date" :rules="['date']" label="Pay Date">
										<template v-slot:append>
											<i class="far fa-calendar-alt cursor-pointer">
												<q-popup-proxy ref="dhDateProxy" transition-show="scale"
													transition-hide="scale">
													<q-date v-model="pay_date" @input="() => $refs.dhDateProxy.hide()"></q-date>
												</q-popup-proxy>
											</i>
										</template>
									</q-input>
								</div>
							</div>

							<div v-if="hasMultiplePayDays" class="hris-form-section" style="margin-top: 16px; box-shadow: none; border-style: dashed;">
								<div class="row items-center q-mb-sm">
									<div class="hris-form-section-title" style="margin: 0;">Payroll Group Schedules</div>
									<q-space></q-space>
									<q-btn flat dense round color="green" icon="fas fa-plus" @click="addGroupSchedule">
										<q-tooltip>Add Group Schedule</q-tooltip>
									</q-btn>
								</div>
								<div v-for="(schedule, index) in group_schedules" :key="index" class="q-pa-sm q-mb-sm" style="border: 1px solid var(--hris-border-light); border-radius: 8px;">
									<div class="row q-col-gutter-xs q-mb-xs items-center">
										<div class="col">
											<q-select
												outlined
												dense
												color="green"
												v-model="schedule.payroll_group"
												:options="payrollGroupOptions"
												label="Payroll Group"
											></q-select>
										</div>
										<div class="col-auto">
											<q-btn flat dense round color="red" icon="fas fa-times" @click="removeGroupSchedule(index)"></q-btn>
										</div>
									</div>
									<div class="row q-col-gutter-xs q-mb-xs">
										<div class="col">
											<q-input outlined dense color="green" v-model="schedule.payroll_cutoff_from" mask="date" label="Cutoff From" :rules="['date']">
												<template v-slot:append>
													<i class="far fa-calendar-alt cursor-pointer">
														<q-popup-proxy :ref="'groupFromProxy'+index" transition-show="scale" transition-hide="scale">
															<q-date v-model="schedule.payroll_cutoff_from" @input="() => $refs['groupFromProxy'+index][0].hide()"></q-date>
														</q-popup-proxy>
													</i>
												</template>
											</q-input>
										</div>
										<div class="col">
											<q-input outlined dense color="green" v-model="schedule.payroll_cutoff_to" mask="date" label="Cutoff To" :rules="['date']">
												<template v-slot:append>
													<i class="far fa-calendar-alt cursor-pointer">
														<q-popup-proxy :ref="'groupToProxy'+index" transition-show="scale" transition-hide="scale">
															<q-date v-model="schedule.payroll_cutoff_to" @input="() => $refs['groupToProxy'+index][0].hide()"></q-date>
														</q-popup-proxy>
													</i>
												</template>
											</q-input>
										</div>
									</div>
									<div class="row q-col-gutter-xs items-center">
										<div class="col">
											<q-input outlined dense color="green" v-model="schedule.payroll_period" label="Payroll Period"></q-input>
										</div>
										<div class="col">
											<q-input outlined dense color="green" v-model="schedule.pay_date" mask="date" label="Pay Date" :rules="['date']">
												<template v-slot:append>
													<i class="far fa-calendar-alt cursor-pointer">
														<q-popup-proxy :ref="'groupPayProxy'+index" transition-show="scale" transition-hide="scale">
															<q-date v-model="schedule.pay_date" @input="() => $refs['groupPayProxy'+index][0].hide()"></q-date>
														</q-popup-proxy>
													</i>
												</template>
											</q-input>
										</div>
									</div>
								</div>
							</div>

							<div class="hris-modal-footer" style="padding: 20px 4px 4px; border-top: none; background: transparent;">
								<q-btn unelevated class="hris-q-outline" label="Close" type="reset" v-close-popup></q-btn>
								<q-btn unelevated class="hris-q-primary" label="Submit" type="submit"></q-btn>
							</div>
						</q-form>
					</div>
				</div>
			</q-dialog>
		</div>
	</div>
</template>

<script>
import axios from 'axios';
import mixins from '../../mixins';
import { exportFile } from 'quasar'
function wrapCsvValue(val, formatFn) {
	let formatted = formatFn !== void 0 ? formatFn(val) : val

	formatted = formatted === void 0 || formatted === null ? '' : String(formatted)

	formatted = formatted.split('"').join('""')
	return `"${formatted}"`
}
export default {
	mixins: [mixins],
	data() {
		return {
			tempID: 0,
			initialPagination: { rowsPerPage: 30 },
			addDialog: false,
			yearfilter: new Date().getFullYear(),
			year: new Date().getFullYear(),
			months: '',
			cut_off: '',
			payroll_cutoff_from: '',
			payroll_cutoff_to: '',
			payroll_period: '',
			pay_date: '',
			group_schedules: [],
			yearOptions: [],
			monthOptions: [{ id: 1, name: 'January' }, { id: 2, name: 'February' }, { id: 3, name: 'March' }, { id: 4, name: 'April' }, { id: 5, name: 'May' }, { id: 6, name: 'June' }, { id: 7, name: 'July' }, { id: 8, name: 'August' }, { id: 9, name: 'September' }, { id: 10, name: 'October' }, { id: 11, name: 'November' }, { id: 12, name: 'December' },],
			cutOffOptions: [{ id: 1, name: 'First Cut Off' }, { id: 2, name: 'Second Cut Off' }],
			payrollGroupOptions: [],

			separator: 'cell',
			spinner: false,
			generatedData: [],
			data: [],
			columns: [
				//{ name: 'years', align: 'center', label: 'Years', field: 'years', sortable: true,  desc:'Years' },
				{ name: 'months', align: 'center', label: 'Months', field: 'months', sortable: true, desc: 'Months' },
				{ name: 'cut_off', align: 'center', label: 'Cut Off', field: 'cut_off', sortable: true, desc: 'Cut Off' },
				{ name: 'payroll_cutoff', align: 'center', label: 'Payroll Cut-Off  (Coverage)', field: 'payroll_cutoff', sortable: true, desc: 'Payroll Cut-Off' },
				{ name: 'payroll_period', align: 'center', label: 'Payroll Period', field: 'payroll_period', sortable: true, desc: 'Payroll Period' },
				{ name: 'paydate', align: 'center', label: 'Pay Date', field: 'paydate', sortable: true, desc: 'Pay Date' },
			],

		}
	},

	created() {
		this.filterData()
		this.yearsList()
		this.fetchPayrollGroups()
	},

	computed: {
		hasMultiplePayDays() {
			return this.userData.features && this.userData.features.includes('multiple-pay-days');
		}
	},

	methods: {
		onSubmit() {
			let formData = new FormData();
			formData.append("tempID", this.tempID);
			formData.append("years", this.year);
			formData.append("months", this.months);
			formData.append("cut_off", this.cut_off);
			formData.append("payroll_cutoff_from", this.payroll_cutoff_from);
			formData.append("payroll_cutoff_to", this.payroll_cutoff_to);
			formData.append("payroll_period", this.payroll_period);
			formData.append("pay_date", this.pay_date);
			formData.append("group_schedules", JSON.stringify(this.group_schedules));
			axios.create({
				baseURL: this.apiUrl,
				headers: { 'Authorization': `Bearer ${this.token}` }
			}).post('/timekeeping/periodic', formData)
				.then((res) => {
					this.tempID = 0
					this.onReset()
					this.filterData()
					this.$q.notify({
						color: 'green-9',
						textColor: 'white',
						message: 'Submitted'
					})
					this.addDialog = false
				});

		},

		onReset() {
			this.months = ''
			this.cut_off = ''
			this.payroll_cutoff_from = ''
			this.payroll_cutoff_to = ''
			this.payroll_period = ''
			this.pay_date = ''
			this.group_schedules = []
			this.tempID = 0
		},

		createCutOff: function () {
			this.addDialog = true
		},

		selectedRow: function (obj) {
			const matchedMonth = this.monthOptions.find(option => option.name === obj.row.months)
			const matchedCutOff = this.cutOffOptions.find(option => option.name === obj.row.cut_off)
			const groupSchedules = Array.isArray(obj.row.group_schedules)
				? obj.row.group_schedules
				: Object.values(obj.row.group_schedules || {})

			this.months = matchedMonth ? matchedMonth.id : ''
			this.cut_off = matchedCutOff ? matchedCutOff.id : ''
			this.payroll_cutoff_from = obj.row.payroll_cutoff_from || ''
			this.payroll_cutoff_to = obj.row.payroll_cutoff_to || ''
			this.payroll_period = obj.row.payroll_period || ''
			this.pay_date = obj.row.paydate || ''
			this.group_schedules = groupSchedules.map(schedule => ({
				payroll_group: schedule.payroll_group || '',
				payroll_cutoff_from: schedule.payroll_cutoff_from || '',
				payroll_cutoff_to: schedule.payroll_cutoff_to || '',
				payroll_period: schedule.payroll_period || '',
				pay_date: schedule.pay_date || '',
			}))
			this.tempID = obj.row.id
			this.addDialog = true

		},

		addGroupSchedule() {
			this.group_schedules.push({
				payroll_group: '',
				payroll_cutoff_from: '',
				payroll_cutoff_to: '',
				payroll_period: '',
				pay_date: '',
			});
		},

		removeGroupSchedule(index) {
			this.group_schedules.splice(index, 1);
		},

		filterData: function () {
			this.spinner = true;
			let formData = new FormData();
			formData.append("year", this.yearfilter);
			axios.create({
				baseURL: this.apiUrl,
				headers: { 'Authorization': `Bearer ${this.token}` }
			}).post(`/timekeeping/periodic/all`, formData)
				.then((res) => {
					//console.log(res.data)
					this.generatedData = res.data.data
					this.spinner = false
				});
		},

		yearsList: function () {
			axios.create({
				baseURL: this.apiUrl,
				headers: { 'Authorization': `Bearer ${this.token}` }
			}).post(`/timekeeping/years/list`)
				.then((res) => {
					this.yearOptions = res.data.data
				});
		},

		fetchPayrollGroups: function () {
			axios.create({
				baseURL: this.apiUrl,
				headers: { 'Authorization': `Bearer ${this.token}` }
			}).get(`/admin/setting/PAYROLL_GROUP`)
				.then((res) => {
					this.payrollGroupOptions = res.data.data.map(item => item.name);
				});
		},

		exportTable: function (fileExtension) {

			const content = [this.columns.map(col => wrapCsvValue(col.label))].concat(
				this.generatedData.map(row => this.columns.map(col => wrapCsvValue(
					typeof col.field === 'function'
						? col.field(row)
						: row[col.field === void 0 ? col.name : col.field],
					col.format
				)).join(','))
			).join('\r\n')

			const status = exportFile(
				`PeriodicTable.${fileExtension}`,
				content,
				`text/${fileExtension}`
			);

			if (status !== true) {
				this.$q.notify({
					message: 'Browser denied file download...',
					color: 'negative',
					icon: 'warning'
				})
			}
		},
	}
}
</script>
<style lang="css" scoped>
.center {
	text-align: center
}
</style>
<style lang="sass" scoped>
	.my-sticky-column-table
		thead tr:nth-child(2) th:nth-child(2)
			background-color: #fff
		td:nth-child(2)
			background-color: #c1f4cd
		th:nth-child(2)
		td:nth-child(2)
			position: sticky
			left: 0
			z-index: 1
</style>
